import type {
  CatalogListItem,
  CatalogParentRef,
} from '@skupreme/api-contracts/core';
import {
  Cell,
  Column,
  HintLabel,
  Image,
  Row,
  TableHeader,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { Link, useNavigate } from 'react-router';
import { AvailableUnitsHeader } from './CatalogTable';
import { Tags } from './CatalogRows';
import {
  CatalogTableFrame,
  type CatalogTablePaging,
} from './CatalogTableFrame';
import { InventoryCell } from './InventoryCell';
import { ItemTitleCell } from './ItemTitleCell';
import { PriceCell } from './PriceCell';

export type ComponentsTableProps = CatalogTablePaging & {
  items: CatalogListItem[];
};

const COLUMN_COUNT = 5;

const detailPath = (id: string) =>
  `/app/catalogs/catalog/${encodeURIComponent(id)}`;

/** How many items use the component, and which on hover. */
function UsageCell({ item }: { item: CatalogListItem }) {
  const parents: CatalogParentRef[] = item.parents ?? [];
  const label = `${CommonUtility.numberWithCommas(parents.length)} item(s)`;

  if (!parents.length) {
    return <span className="sk:tabular-nums">{label}</span>;
  }

  return (
    <HintLabel
      placement="bottom start"
      className="sk:whitespace-nowrap sk:tabular-nums"
      hint={
        <ul className="sk:m-0 sk:flex sk:max-h-96 sk:w-80 sk:list-none sk:flex-col sk:gap-2 sk:overflow-auto sk:p-0">
          {parents.map(parent => (
            <li key={parent._id} className="sk:flex sk:items-center sk:gap-2">
              <Image
                src={parent.p_images?.display_image}
                alt=""
                size="sm"
                fit="contain"
              />
              <span className="sk:flex sk:min-w-0 sk:flex-col">
                <span className="sk:truncate sk:text-dense">
                  {parent.p_title}
                </span>
                <span className="sk:truncate sk:text-xs sk:text-fg-tertiary">
                  {parent.p_sku}
                </span>
              </span>
            </li>
          ))}
        </ul>
      }
    >
      {label}
    </HintLabel>
  );
}

/* The same columns, in the same widths, as the items table beside it. */
export function ComponentsTable({ items, ...paging }: ComponentsTableProps) {
  const navigate = useNavigate();

  return (
    <CatalogTableFrame
      aria-label="Components"
      columnCount={COLUMN_COUNT}
      header={
        <TableHeader>
          <Column id="p_sku" isRowHeader allowsSorting className="sk:w-[38%]">
            Product
          </Column>
          <Column id="price" className="sk:w-[10%]">
            Price
          </Column>
          <Column id="tags" className="sk:w-[14%]">
            Tags
          </Column>
          <Column id="total_inventory" allowsSorting className="sk:w-[12%]">
            <AvailableUnitsHeader />
          </Column>
          <Column id="usage" className="sk:w-[26%]">
            Component usage
          </Column>
        </TableHeader>
      }
      rows={items.map(item => (
        <Row
          key={item._id}
          id={item._id}
          onAction={() => navigate(detailPath(item._id))}
        >
          <Cell>
            <div className="sk:flex sk:items-center sk:gap-3">
              <Link to={detailPath(item._id)} className="sk:block sk:shrink-0">
                <Image
                  src={item.p_images?.display_image}
                  alt=""
                  size="md"
                  fit="contain"
                />
              </Link>
              <ItemTitleCell item={item} showState={false} />
            </div>
          </Cell>
          <Cell className="sk:whitespace-nowrap">
            <PriceCell item={item} />
          </Cell>
          <Cell>
            <Tags tags={item.tags} />
          </Cell>
          <Cell>
            <InventoryCell item={item} />
          </Cell>
          <Cell>
            <UsageCell item={item} />
          </Cell>
        </Row>
      ))}
      {...paging}
    />
  );
}
